import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { auth } from '../firebase';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign up function
  const signup = async (email, password, displayName) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName && userCredential.user) {
      await updateProfile(userCredential.user, { displayName });
    }
    return userCredential;
  };

  // Login function
  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  // Logout function
  const logout = () => {
    return signOut(auth);
  };

  // Password reset
  const resetPassword = (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  // Fetch or create user profile
  const fetchUserProfile = async (user) => {
    if (!user) {
      setUserProfile(null);
      return;
    }

    try {
      // Try to get existing profile
      const response = await axios.get(`${API}/users/profile/${user.uid}`);
      setUserProfile(response.data);
      console.log('✅ User profile loaded:', response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        // Create new profile if not found
        console.log('📝 Creating new user profile...');
        try {
          const createResponse = await axios.post(`${API}/users/profile`, {
            user_id: user.uid,
            email: user.email,
            display_name: user.displayName
          });
          setUserProfile(createResponse.data);
          console.log('✅ User profile created:', createResponse.data);
        } catch (createError) {
          console.error('❌ Error creating user profile:', createError);
          console.error('Response:', createError.response?.data);
          // Set a minimal profile so user can still use the app
          setUserProfile({
            user_id: user.uid,
            email: user.email,
            display_name: user.displayName,
            subscription_tier: 'free',
            recording_count_current_month: 0
          });
        }
      } else {
        console.error('❌ Error fetching user profile:', error);
        // Set a minimal profile so user can still use the app
        setUserProfile({
          user_id: user.uid,
          email: user.email,
          display_name: user.displayName,
          subscription_tier: 'free',
          recording_count_current_month: 0
        });
      }
    }
  };

  // Monitor auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      await fetchUserProfile(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    logout,
    resetPassword,
    loading,
    isPro: userProfile?.subscription_tier === 'pro'
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
